import { useState, useCallback, useRef, useMemo } from "react";
import { useBreakpointValue } from "@chakra-ui/react";
import { FiTrendingUp } from "react-icons/fi";
import { cn, showToast } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { AutoSaveIndicator } from "newui/components/ui/AutoSave/AutoSaveIndicator";
import { useSupplierScoring } from "../../../hooks/useSupplierScoring";
import { useScoringInstructions } from "../../../hooks/useScoringInstructions";
import { ScoringModalHeader } from "./ScoringModalHeader";
import { ScoringModalDescriptionBar } from "./components/ScoringModalDescriptionBar";
import { ScoringProgressBar } from "./components/ScoringProgressBar";
import { CriterionScoringRow } from "./CriterionScoringRow";
import { ScoringInstructionsPanel } from "./ScoringInstructionsPanel";
import { EvaluationClarificationModal } from "./panel/clarifications";
import type { SupplierEvaluationSummary, UnsavedChangesAction } from "../../../types/scoring.type";
import { useAutoSave } from "newui/hooks/useAutoSave";
import { SelectOption, SelectWithLabel } from "newui/components/ui/InputWithLabel";

interface ScoringModalProps {
	evalId?: number;
	evaluatorId?: number;
	isOpen: boolean;
	readOnly?: boolean;
	rfxId: number;
	selectedInviteId: number;
	suppliers: SupplierEvaluationSummary[];
	onClose: () => void;
	onScoringComplete: () => void;
	onSupplierChange: (inviteId: number) => void;
}

export const ScoringModal: React.FC<ScoringModalProps> = ({
	isOpen,
	rfxId,
	evalId,
	suppliers,
	selectedInviteId,
	evaluatorId,
	readOnly = false,
	onClose,
	onSupplierChange,
	onScoringComplete,
}) => {
	const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [showClarificationModal, setShowClarificationModal] = useState(false);
	const [instructionsPanelOpen, setInstructionsPanelOpen] = useState(false);
	const [focusedScore, setFocusedScore] = useState<number | null>(null);
	const [focusedElement, setFocusedElement] = useState<HTMLDivElement | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const pendingAction = useRef<(() => void) | null>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	/**
	 * Focus target on open. Without it Chakra focuses the first tabbable node, which is
	 * the company logo's Tooltip wrapper (CompanyLogoDisplay uses shouldWrapChildren, so
	 * Chakra injects a focusable span). Focusing it OPENS the tooltip, whose label is a
	 * large FallbackImage — hence the logo appearing blown up on load as if hovered.
	 * The supplier dropdown is the first control a scorer actually wants.
	 */
	const supplierSelectRef = useRef<{ focus: () => void }>(null);
	const userDismissedGuide = useRef(false);

	const supplierOptions = useMemo(() => {
		return suppliers.map((s) => ({
			value: s.inviteId,
			label: s.supplierName,
			logo: s.supplierLogo
		}));
	}, [suppliers]);

	// The next supplier that still needs scoring (any supplier other than the one
	// currently open whose scoring isn't yet complete). When present we surface a
	// "Save and Continue" primary action; when absent "Save and Close" is primary.
	const nextSupplierToScore = useMemo(
		() =>
			suppliers.find(
				(s) => s.inviteId !== selectedInviteId && s.status !== "done"
			),
		[suppliers, selectedInviteId]
	);

	const {
		scoringDetail,
		isLoading: isLoadingScores,
		isDirty,
		lastSavedAt: scoringLastSavedAt,
		percentComplete,
		getDirtyCriteriaIds,
		loadSupplierScores,
		updateCriterionScore,
		saveCriterion,
	} = useSupplierScoring({ rfxId, evaluatorId });

	const { instructions, isLoading: isLoadingInstructions } = useScoringInstructions({
		rfxId,
	});

	const maxScore = instructions?.totalScore ?? 10;

	// Persists every dirty criterion. Returns `true` when everything saved (or
	// there was nothing to save), `false` if any save failed — callers use this to
	// decide whether it is safe to close / advance.
	const saveDirtyFields = useCallback(async (): Promise<boolean> => {
		const dirtyIds = getDirtyCriteriaIds();
		if (dirtyIds.length === 0) {
			return true;
		}
		setSaveError(null);
		for (const criteriaId of dirtyIds) {
			const success = await saveCriterion(criteriaId);
			if (!success) {
				setSaveError("Failed to save some changes");
				return false;
			}
		}
		// Refresh supplier progress in background after successful save
		onScoringComplete();
		return true;
	}, [getDirtyCriteriaIds, saveCriterion, onScoringComplete]);

	const { isSaving, lastSavedAt } = useAutoSave({
		isDirty,
		onSave: saveDirtyFields,
		debounceMs: 2500,
	});

	const handleSupplierChange = useCallback(
		(invite: SelectOption) => {
			const inviteId = invite.value;
			if (isDirty) {
				pendingAction.current = () => {
					onSupplierChange(inviteId);
					loadSupplierScores(inviteId);
				};
				setShowConfirmDialog(true);
				return;
			}
			onSupplierChange(inviteId);
			loadSupplierScores(inviteId);
		},
		[isDirty, onSupplierChange, loadSupplierScores]
	);

	const handleClose = useCallback(() => {
		if (isDirty) {
			pendingAction.current = () => {
				onScoringComplete();
				onClose();
			};
			setShowConfirmDialog(true);
			return;
		}
		onScoringComplete();
		onClose();
	}, [isDirty, onClose, onScoringComplete]);

	// "Save and Close": flush any pending changes first. Only close once the save
	// succeeds; on failure keep the modal open, surface the Unsaved Changes prompt
	// and an error toast so the user doesn't lose work.
	const handleSaveAndClose = useCallback(async () => {
		setIsSubmitting(true);
		try {
			const saved = await saveDirtyFields();
			if (!saved) {
				showToast(
					"Your scoring changes could not be saved. Please try again before closing.",
					"Unable to save changes",
					false,
					"scoring-save-close-error"
				);
				pendingAction.current = () => {
					onScoringComplete();
					onClose();
				};
				setShowConfirmDialog(true);
				return;
			}
			onScoringComplete();
			onClose();
		} finally {
			setIsSubmitting(false);
		}
	}, [saveDirtyFields, onScoringComplete, onClose]);

	// "Save and Continue": save the current supplier, then advance to the next
	// supplier still needing scoring without closing the modal.
	const handleSaveAndContinue = useCallback(async () => {
		if (!nextSupplierToScore) {
			return;
		}
		setIsSubmitting(true);
		try {
			const saved = await saveDirtyFields();
			if (!saved) {
				showToast(
					"Your scoring changes could not be saved. Please try again.",
					"Unable to save changes",
					false,
					"scoring-save-continue-error"
				);
				return;
			}
			onSupplierChange(nextSupplierToScore.inviteId);
			loadSupplierScores(nextSupplierToScore.inviteId);
		} finally {
			setIsSubmitting(false);
		}
	}, [
		nextSupplierToScore,
		saveDirtyFields,
		onSupplierChange,
		loadSupplierScores,
	]);

	const handleConfirmAction = useCallback(
		async (action: UnsavedChangesAction) => {
			setShowConfirmDialog(false);
			if (action === "cancel") {
				pendingAction.current = null;
				return;
			}
			if (action === "save") {
				await saveDirtyFields();
			}
			if (pendingAction.current) {
				pendingAction.current();
				pendingAction.current = null;
			}
		},
		[saveDirtyFields]
	);

	const handleScoreChange = useCallback(
		(rfxCriteriaId: number, score: number | null) => {
			updateCriterionScore(rfxCriteriaId, "score", score);
			setFocusedScore(score);
		},
		[updateCriterionScore]
	);

	const handleCommentChange = useCallback(
		(rfxCriteriaId: number, comment: string | null) => {
			updateCriterionScore(rfxCriteriaId, "comment", comment);
		},
		[updateCriterionScore]
	);

	const handleToggleGuide = useCallback(() => {
		setInstructionsPanelOpen((prev) => {
			userDismissedGuide.current = prev;
			return !prev;
		});
	}, []);

	const handleCloseGuide = useCallback(() => {
		userDismissedGuide.current = true;
		setInstructionsPanelOpen(false);
	}, []);

	const handleRowFocus = useCallback(
		(rfxCriteriaId: number, element: HTMLDivElement) => {
			setFocusedElement(element);
			// Update focusedScore to the current score for this criterion
			if (scoringDetail) {
				const criterion = scoringDetail.scores.find((s) => s.rfxCriteriaId === rfxCriteriaId);
				if (criterion) {
					setFocusedScore(criterion.score);
				}
			}
			// Auto-show the guide on desktop when user focuses a row
			if (window.innerWidth >= 768 && !userDismissedGuide.current) {
				setInstructionsPanelOpen(true);
			}
		},
		[scoringDetail]
	);

	// Load scores when modal opens with a selected supplier
	const prevInviteId = useRef<number | null>(null);
	if (isOpen && selectedInviteId !== prevInviteId.current) {
		prevInviteId.current = selectedInviteId;
		loadSupplierScores(selectedInviteId);
	}

	if (!isOpen) {
		return null;
	}

	const modalTitle = "Score Supplier";

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				title={!isMobile && modalTitle}
				titleColor="blue.800"
				hideCloseButton={isMobile}
				trapFocus={!showClarificationModal}
				initialFocusRef={supplierSelectRef}
				customIcon={<FiTrendingUp size={30} className="fill-white stroke-blue-700" />}
				description={
					<ScoringModalDescriptionBar
						suppliers={suppliers}
						selectedInviteId={selectedInviteId}
						supplierOptions={supplierOptions}
						supplierSelectRef={supplierSelectRef as unknown as React.Ref<never>}
						onSupplierChange={handleSupplierChange}
						isMobile={isMobile}
						isSaving={isSaving}
						lastSavedAt={lastSavedAt ?? scoringLastSavedAt}
						saveError={saveError}
						instructionsPanelOpen={instructionsPanelOpen}
						onAddClarification={evalId ? () => setShowClarificationModal(true) : undefined}
						onToggleGuide={handleToggleGuide}
					/>
				}
				wrapDescription
				headerAccessory={<ScoringProgressBar percentComplete={percentComplete} />}
				size="full"
				closeOnEsc={!isDirty}
				closeOnOverlayClick={false}
				customFooter={
					<div className="flex flex-wrap items-center justify-end gap-3 w-full px-4">
						<AutoSaveIndicator
							isSaving={isSaving}
							lastSavedAt={lastSavedAt ?? scoringLastSavedAt}
							error={saveError}
							size="xs"
						/>
						<Button
							btntype={nextSupplierToScore ? "secondary" : "primary"}
							size="xs"
							onClick={handleSaveAndClose}
							isLoading={isSubmitting}
							data-testid="scoring-modal-close-btn"
						>
							Save and Close
						</Button>
						{nextSupplierToScore && (
							<Button
								btntype="primary"
								size="xs"
								onClick={handleSaveAndContinue}
								isLoading={isSubmitting}
								data-testid="scoring-modal-continue-btn"
							>
								Save and Continue
							</Button>
						)}
					</div>
				}
			>
				<div
					ref={contentRef}
					className="relative space-y-6 p-4"
					data-testid="scoring-modal-content"
				>
					{isMobile && <div className="md:hidden" data-testid="supplier-select-mobile">
						<SelectWithLabel label="Supplier" className={isMobile && "text-sm"} value={selectedInviteId} options={supplierOptions} onChange={handleSupplierChange} data-testid="supplier-select-mobile" />
					</div>}

					<ScoringModalHeader
						suppliers={suppliers}
						selectedInviteId={selectedInviteId}
						rfxId={rfxId}
						readOnly={readOnly}
					/>

					{isLoadingScores && (
						<div
							className={cn("space-y-4", instructionsPanelOpen && "pb-[40vh] md:pb-[50vh]")}
							data-testid="scoring-loading"
							aria-busy="true"
							aria-live="polite"
						>
							{Array.from({ length: scoringDetail?.scores.length ?? 3 }).map((_, i) => (
								<SkeletonBase
									key={i}
									startColor="lightgray"
									width="100%"
									height="148px"
									className="!rounded-lg"
								/>
							))}
						</div>
					)}

					{!isLoadingScores && scoringDetail && (
						<div className={cn("space-y-4", instructionsPanelOpen && "pb-[40vh] md:pb-[50vh]")} data-testid="scoring-grid">
							{scoringDetail.scores.map((criterion) => (
								<CriterionScoringRow
									key={criterion.rfxCriteriaId}
									criterion={criterion}
									maxScore={maxScore}
									readOnly={readOnly}
									onScoreChange={handleScoreChange}
									onCommentChange={handleCommentChange}
									onFocus={handleRowFocus}
									showGuideHint={!instructionsPanelOpen}
									onShowGuide={() => {
										userDismissedGuide.current = false;
										setInstructionsPanelOpen(true);
									}}
								/>
							))}
						</div>
					)}

					{!isLoadingScores && !scoringDetail && (
						<div className="text-center py-8 text-sm text-gray-400" data-testid="scoring-empty">
							No scoring data available for this supplier.
						</div>
					)}

					<ScoringInstructionsPanel
						instructions={instructions}
						isLoading={isLoadingInstructions}
						activeScore={focusedScore}
						isOpen={instructionsPanelOpen}
						onToggle={handleCloseGuide}
						anchorElement={focusedElement}
						containerElement={contentRef.current}
					/>
				</div>
			</Modal>

			{showClarificationModal && evalId && (
				<EvaluationClarificationModal
					rfxId={rfxId}
					evalId={evalId}
					isOpen={showClarificationModal}
					suppliers={suppliers}
					preSelectedInviteId={selectedInviteId}
					showSubmitAndAnother
					onClose={() => setShowClarificationModal(false)}
				/>
			)}

			{showConfirmDialog && (
				<Modal
					isOpen={showConfirmDialog}
					onClose={() => handleConfirmAction("cancel")}
					title="Unsaved Changes"
					size="md"
					iconType="warning"
					modalType="alert"
					closeOnEsc
					closeOnOverlayClick
					customFooter={
						<div className="flex w-full justify-center gap-3">
							<button
								type="button"
								onClick={() => handleConfirmAction("cancel")}
								className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => handleConfirmAction("save")}
								className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
							>
								Save &amp; Continue
							</button>
						</div>
					}
				>
					<p className="text-base text-gray-600 text-center">
						You have unsaved scoring changes.
						<br /><br />
						Would you like to save them before continuing?
					</p>
				</Modal>
			)}
		</>
	);
};
